import React, {useEffect, useState} from "react";
import AppConfig from "../../helpers/config";
import "rc-slider/assets/index.css";
import "./styles.scss";
import {useSelector} from "react-redux";
import {getLoginConfig} from "../../redux/slices/loginconfigSlice";
import TooltipSlider from "../TooltipSlider";
import PropTypes from "prop-types";
import useTranslationCustom from "../../i18n/customHook/useTranslation";

// maxPrice is the top of the track, not a filter: callers that need a different
// ceiling (the chat's Stay only step) pass their own; everyone else gets AppConfig's.
function RangeBar({lowPrice, highPrice, onChangePriceHandler = null, disableBG = false, disabled = false, maxPrice = AppConfig.maxPrice, perStay = false}) {
    const maxAllowedPrice = 1;
    const {T} = useTranslationCustom();
    const loginconfig = useSelector(getLoginConfig);
    // A band wider than the track cannot be drawn: without this the bar fills end to
    // end while the readout shows the out-of-range figure, and the first drag snaps it
    // down. Owners of the band clamp it too; this keeps the slider honest regardless.
    const clampToTrack = (value, fallback) => {
        const number = Number(value);
        return Math.min(Math.max(Number.isFinite(number) ? number : fallback, AppConfig.minPrice), maxPrice);
    };
    const [priceRange, setPriceRange] = useState({
        low: clampToTrack(lowPrice, AppConfig.minPrice),
        high: clampToTrack(highPrice, maxPrice),
    });

    useEffect(() => {
        setPriceRange({
            low: clampToTrack(lowPrice, AppConfig.minPrice),
            high: clampToTrack(highPrice, maxPrice),
        });
    }, [lowPrice, highPrice, maxPrice]);

    // A Stay only band buys the whole stay, so "p.p" (per person) would misstate it.
    // The dedicated string is used when the CMS has it; until then the caption is the
    // plain price label, never the per-person one.
    const perStayCaption = perStay ? (T("Common:Price_Per_Stay_Rangebar") || T("Common:Price")) : null;

    return (
        <div className={perStayCaption ? "range-bar range-bar--per-stay" : "range-bar"} style={disableBG ? {backgroundColor: 'darkgray'} : {}}>
            {/* The per-stay caption is longer than "Price p.p" and would run into the
                tiles when overlaid, so it sits in the flow above them instead. */}
            <div className={perStayCaption ? "caption caption--stacked" : "caption"}>
                <p>
                    {perStayCaption ? perStayCaption : (
                        <>
                            {T("Common:Price")}
                            <span> p.p</span>
                        </>
                    )}
                </p>
            </div>
            <div className="range-container">
                <p className="range-value highest">
                    {loginconfig?.currency?.symbol}
                    {priceRange?.low?.toFixed(0)}
                </p>
                <div className="range-slider-holder">
                    <div className="tiles">
                        {AppConfig.tileValues(AppConfig.minPrice, maxPrice).map(
                            (tile, index) => (
                                <Tile
                                    key={index}
                                    low={priceRange.low}
                                    high={priceRange.high}
                                    tile={tile}
                                />
                            )
                        )}
                    </div>
                    <div className="outerslider-container">
                        <TooltipSlider
                            range
                            step={1}
                            min={AppConfig.minPrice}
                            max={maxPrice}
                            defaultValue={[AppConfig.minPrice, maxPrice]}
                            value={[priceRange.low, priceRange.high]}
                            onChange={(value) => {
                                if (disabled) return;
                                if (value[1] < maxAllowedPrice) {
                                    setPriceRange({low: value[0], high: maxAllowedPrice});
                                    return;
                                }
                                setPriceRange({low: value[0], high: value[1]});
                            }}
                            onChangeComplete={(value) => {
                                if (disabled) return;
                                if (value[1] < maxAllowedPrice) {
                                    onChangePriceHandler({
                                        low: value[0],
                                        high: maxAllowedPrice,
                                    });
                                    return;
                                }
                                onChangePriceHandler({low: value[0], high: value[1]});
                            }}
                            style={{padding: 0}}
                            handleStyle={{
                                backgroundColor: "#fff",
                                borderColor: "white",
                                width: "18px",
                                height: "18px",
                                marginTop: "-6px",
                                cursor: "pointer",
                                zIndex: 1,
                                opacity: "100%",
                            }}
                            trackStyle={{
                                backgroundColor: "var(--green)",
                            }}
                            activeDotStyle={{
                                borderWidth: "0px",
                                borderColor: "white",
                            }}
                            dotStyle={{height: "20px"}}
                        />
                    </div>
                </div>
                <p className="range-value highest">
                    {loginconfig?.currency?.symbol}
                    {priceRange?.high?.toFixed(0)}
                </p>
            </div>
        </div>
    );
}

const Tile = ({low, high, tile}) => {
    return (
        <div
            className={
                tile.value >= low && tile.value <= high
                    ? "default-bg-color"
                    : "filled-bg-color"
            }
            style={{
                height: tile.height,
                width: 4,
                borderRadius: 2,
                backgroundColor:
                    tile.value >= low && tile.value <= high
                        ? 'var(--green)'
                        : "#00000066",
            }}
        />
    );
};

Tile.propTypes = {
    low: PropTypes.number,
    high: PropTypes.number,
    tile: PropTypes.object,
}

RangeBar.propTypes = {
    lowPrice: PropTypes.number,
    highPrice: PropTypes.number,
    onChangePriceHandler: PropTypes.func,
    disableBG: PropTypes.bool,
    disabled: PropTypes.bool,
    maxPrice: PropTypes.number,
    perStay: PropTypes.bool,
}

export default RangeBar;
